import store from "../store/store";
import firebase from "./FirebaseConfig";

export const useAssetsKeyService = () => {
  const collection = "assets_access_keys";

  function getLoggedUserEmail() {
    return store.getters.user.data.email;
  }

  /**
   * 
   *  Creates a new key document on firestore. The missing "key" field will be generated by pub/sub trigger function on GCP.
   * 
   * @param {string} description : A description for the key (optional)
   * @param {number} usageLimit : The maximum number of times the key can be used (required, must be a positive integer)
   * @returns {Object} Returns an object with `data` and `error` properties. The `data` property contains a success message if the key was created successfully, or null if there was an error. The `error` property contains an error message if there was an error, or null if the key was created successfully.
   */
  async function createKey(description, usageLimit) {
    try {

        const usage = parseInt(usageLimit);

      if (isNaN(usage) || usage < 1) {
        throw new Error("Limite de uso deve ser um número positivo.");
      }

      const userEmail = getLoggedUserEmail();
      if (!userEmail) {
        throw new Error("Usuário não autenticado.");
      }

      const doc = firebase.firestore().collection(collection).doc();
      const id = doc.id;

      const baseDoc = {
        id: id,
        created_at: new Date(),
        created_by: userEmail,
        active: true,
        usage: [],
        usageLimit: usage,
        description: description,
      };
      
      await doc.set(baseDoc);
      return{
        data: "Chave criada com sucesso.",
        error: null,
      }

    } catch (error) {
      console.log("Error creating key:", error);
      return {
        data: null,
        error: error.message || "Ocorreu um erro ao criar a chave.",
      };
    }
  }

  /**
   * Edits an existing key.
   * @param {string} id
   * @param {string} description
   * @param {number} usageLimit
   * @param {boolean} active
   * @returns {string|null} Returns null if successful, otherwise returns an error message.
   */
  async function editKey(id, description, usageLimit, active) {
    try {
      const usage = parseInt(usageLimit);

      if (!id) {
        return "ID da chave inválido para edição.";
      }

      if (isNaN(usage) || usage < 1) {
        return "Limite de uso deve ser um número positivo.";
      }

      const doc = firebase.firestore().collection(collection).doc(id);
      await doc.update({
        description: description,
        usageLimit: usage,
        active: active,
      });
      return null;
    } catch (error) {
      return error.message || "Ocorreu um erro ao editar a chave.";
    }
  }

  /**
   * Fetches keys from firestore.
   * @returns an object {keys: Array | null, error: String | null}
   */
  async function fetchKeys() {
    try {
      const snapshot = await firebase
        .firestore()
        .collection(collection)
        .orderBy("created_at", "desc")
        .get();
      if (snapshot.empty) {
        return {
          keys: [],
          error: null,
        };
      }
      const docs = snapshot.docs.map((doc) => doc.data());

      const keys = docs.map((doc) => {
        return {
          ...doc,
          key: doc.key,
          created_at: doc.created_at.toDate(),
          usage: doc.usage
            ? doc.usage.map((u) => ({ ...u, date: u.date.toDate() }))
            : [],
        };
      });

      return {
        keys: keys,
        error: null,
      };
    } catch (error) {
      const message =
        error.message == "Missing or insufficient permissions."
          ? "Você não tem permissão para acessar as chaves."
          : error.message;
      return {
        keys: null,
        error: message || "Ocorreu um erro ao buscar as chaves.",
      };
    }
  }

  async function deleteKey(id) {
    const doc = firebase.firestore().collection(collection).doc(id);
    await doc.delete();
  }

  return {
    createKey,
    editKey,
    fetchKeys,
    deleteKey,
  };
};
